import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AiUnderwritingCtaAlert } from '@/shared/components/ai-underwriting-cta-alert';
import { UnderwritingSlideOut } from '@/components/UnderwritingSlideOut';
import {
  AdjustedQuotesBanner,
  DEFAULT_FILTERS_STATE,
  GapFillBanner,
  QuoteErrorDisplay,
  ShopInsuranceAcknowledgmentOverlay,
  ShopInsuranceContext,
  ShopInsuranceFiltersSidebar,
  ShopInsuranceHeader,
  ShopInsuranceNotice,
  ShopInsuranceTabContent,
  ShopInsuranceTabsWrapper,
  getHideSinglePayAnd10Pay,
  getShowSinglePayAnd10Pay,
  getShowTraditionalLTCOptions,
  mapMaritalStatusToTraditionalLTC,
  useBenefitDesignInputs,
  useEligibilityFiltersActions,
  useQuoteFetching,
  useQuoteSelection,
  useSelectedEligibilityFilters,
  useShopInsuranceAcknowledgment,
  useShopInsuranceModeQuotes,
  useShopInsuranceState,
  useShopInsuranceTraditionalLTC,
  useShopInsuranceUnderwriting,
  useSliderBounds,
  useSliderBoundsSync,
  type CombinedFilterType,
  type ShopInsuranceModalPropsOptions,
} from '@/features/shop-insurance';
import {
  AddPartnerDialog,
  LinkExistingPartnerDialog,
  PartnerRequiredDialog,
} from '@/features/add-partner';
import { PoliciesComparisonModal } from '@/features/policies-comparison';
import { Button } from '@design-system';

import { Loader } from 'lucide-react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useDebounce } from '@/hooks/useDebounce';
import { useErrorToasts } from '@/hooks/useErrorToasts';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  selectActiveFundingSourceOperation,
  selectAllPaymentOptionQuotes,
  selectClient,
  selectClientCalculationSettings,
  selectFeatureFlags,
  selectMaybeClientPartner,
  selectQuotes,
  selectQuotesError,
  selectQuotesIsLoading,
  selectSession,
  selectSessionAdvisor,
} from '@/model';
import { isKnownLimitationError, isUnexpectedError } from '@/util/quoteTypeGuards';
import { NoQuotesNYScreen } from '@/components/NoQuotesNYScreen';
import { SessionType, zipCodeToState } from '@shared';

interface FormData {
  premium: number;
  benefitDuration: CombinedFilterType;
  inflationProtection: CombinedFilterType;
}

export function ShopInsuranceContent({
  onClose,
  queryOptions,
}: {
  onClose: () => void;
  queryOptions?: ShopInsuranceModalPropsOptions;
}) {
  const navigate = useNavigate();
  const [showAddPartnerDialog, setShowAddPartnerDialog] = useState(false);
  const [showLinkExistingDialog, setShowLinkExistingDialog] = useState(false);
  const [showComparePolicies, setShowComparePolicies] = useState(false);
  const { sessionType } = useSelector(selectSession);
  const isAdvisor = sessionType === SessionType.advisor || sessionType === SessionType.wizard;
  const { flags } = useFeatureFlags();
  const skipAutoFetch = queryOptions?.skipAutoFetch ?? false;
  useErrorToasts({ open: true });

  const quotes = useSelector(selectQuotes);
  const allPaymentOptionQuotes = useSelector(selectAllPaymentOptionQuotes);
  const client = useSelector(selectClient);
  const { advisorId } = useSelector(selectSessionAdvisor);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const state = zipCodeToState(client.intakeSurvey.clientZipCode);

  const selectedEligibilityFilters = useSelectedEligibilityFilters();
  const { removeEligibilityFilter, removeFeatureBenefitsFilter } = useEligibilityFiltersActions();

  const error = useSelector(selectQuotesError);
  const isLoading = useSelector(selectQuotesIsLoading);
  const featureFlags = useSelector(selectFeatureFlags);
  const isD2C = sessionType === SessionType.client && !!featureFlags.d2cMode;

  const {
    selectedTab,
    setSelectedTab,
    paymentOption,
    mode,
    expandedIndices,
    setExpandedIndices,
    hasAcknowledged,
    showAcknowledgment,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    hasSameGenderPartner,
    showPartnerRequiredDialog,
    setShowPartnerRequiredDialog,
    handleTabChange,
    handleCardToggle,
    handleAcknowledgment,
    setShowAcknowledgment,
    handleBuyerOptionChange,
    handlePaymentOptionChange,
    handleModeChange,
  } = useShopInsuranceState({
    hideSinglePayAnd10Pay: getHideSinglePayAnd10Pay(
      featureFlags.hideSinglePayAnd10Pay,
      client.intakeSurvey.clientBirthDate,
    ),
    providedBuyerOption: queryOptions?.buyerOption,
    providedPaymentOption: queryOptions?.paymentOption,
    providedSelectedTab: queryOptions?.selectTabOption,
  });

  const { fetchQuotesForPolicyType } = useQuoteFetching({
    open: true,
    selectedTab,
    paymentOption,
    hasPartnerWithDemographics,
    skipAutoFetch,
  });

  const sliderBounds = useSliderBounds(quotes, selectedTab);
  const { isQuoteSelected } = useQuoteSelection({ selectedTab });

  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const formMethods = useForm<FormData>({
    defaultValues: {
      ...DEFAULT_FILTERS_STATE,
      premium: sliderBounds.defaultValue,
    },
  });

  const { watch, reset, getValues } = formMethods;

  const premiumValue = useDebounce(watch('premium'), 200);
  const benefitDurationValue = useDebounce(watch('benefitDuration'), 200);
  const inflationProtectionValue = useDebounce(watch('inflationProtection'), 200);

  const totalCost = client.allPhaseCosts.allPhaseInflatedProfessionalShareCost;
  const [isGapFillMode, setIsGapFillMode] = useState(false);
  const setGapFillMode = useCallback((active: boolean) => setIsGapFillMode(active), []);
  const isCoverageGapEnabled = flags.coverageGap ?? false;

  useSliderBoundsSync({ sliderBounds, paymentOption, reset, getValues });

  const benefitDesignInputs = useBenefitDesignInputs();

  const { filteredTraditionalLTCQuotes } = useShopInsuranceTraditionalLTC({
    paymentOption,
    selectedBuyerOption,
    hasPartnerWithDemographics,
    advisorId,
    returnOnInvestmentCalculationType,
  });

  const {
    adjustedQuotes,
    isAdjusted,
    benefitDesignQuoteGroups,
    benefitDesignAvailableOptions,
    modeAdjustedQuotes,
    modeTraditionalLTCQuotes,
    selectedFeatureBenefitsFilters,
  } = useShopInsuranceModeQuotes({
    mode,
    selectedTab,
    client,
    quotes,
    allPaymentOptionQuotes,
    premiumValue,
    benefitDurationValue,
    inflationProtectionValue,
    benefitDesignInputs,
    returnOnInvestmentCalculationType,
    hasPartnerWithDemographics,
    filteredTraditionalLTCQuotes,
    isGapFillMode,
    totalCost,
  });

  useShopInsuranceAcknowledgment({
    open: true,
    isLoading,
    quotes,
    hasAcknowledged,
    setShowAcknowledgment,
  });

  const {
    underwritingFetchStarted,
    underwritingFetchComplete,
    underwritingResults,
    eligibilityCounts,
    filteredAdjustedQuotes,
    filteredTraditionalLTCQuotesWithEligibility,
    showEligibilityFilter,
    showUnderwritingSlideOut,
    setShowUnderwritingSlideOut,
    selectedUnderwritingData,
    handleViewUnderwritingDetails,
  } = useShopInsuranceUnderwriting({
    clientId: client?.clientId,
    selectedTab,
    adjustedQuotes: modeAdjustedQuotes,
    traditionalLTCQuotes: modeTraditionalLTCQuotes,
  });

  function handleTabChangeWithFetch(tabValue: string) {
    const tabChanged = handleTabChange(tabValue);
    if (tabChanged) {
      fetchQuotesForPolicyType(tabValue as ShopInsuranceTab, paymentOption);
    }
  }

  function handleOnClose() {
    localStorage.removeItem('benefitDurationNotice');
    localStorage.removeItem('inflationProtectionNotice');
    onClose();
  }

  const activeFundingSourceOperation = useSelector(selectActiveFundingSourceOperation);
  const hasSelectedPolicies =
    Object.keys(client?.multipleFundingSources?.ltcPolicy ?? {}).length > 0;
  const hasQuotesLoaded = (quotes?.length ?? 0) > 0;
  const isReloading = activeFundingSourceOperation !== null;
  const isInitialLoading = isLoading && !hasQuotesLoaded;
  const hasUnexpectedError = isUnexpectedError(error);
  const knownLimitationError = isKnownLimitationError(error) ? error : null;
  const isNYStateNotSupported =
    state.stateAbbreviation === 'NY' && error?.type === 'STATE_NOT_SUPPORTED';
  const showContentArea = !isInitialLoading && !hasUnexpectedError && !isNYStateNotSupported;

  const tabContent = useMemo(
    () => (
      <ShopInsuranceTabContent
        underwritingFetchStarted={underwritingFetchStarted}
        underwritingFetchComplete={underwritingFetchComplete}
        filteredAdjustedQuotes={filteredAdjustedQuotes}
        filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
        underwritingResults={underwritingResults}
        expandedIndices={expandedIndices}
        selectedTab={selectedTab}
        clientPartner={clientPartner}
        premiumValue={premiumValue}
        paymentOption={paymentOption}
        advisorId={advisorId}
        isQuoteSelected={isQuoteSelected}
        handleCardToggle={handleCardToggle}
        handleViewUnderwritingDetails={handleViewUnderwritingDetails}
        onClose={handleOnClose}
        knownLimitationError={knownLimitationError}
        hasSameGenderPartner={hasSameGenderPartner}
        mode={mode}
        benefitDesignQuoteGroups={benefitDesignQuoteGroups}
        stateOfResidence={state.stateAbbreviation}
      />
    ),
    [
      underwritingFetchStarted,
      underwritingFetchComplete,
      filteredAdjustedQuotes,
      filteredTraditionalLTCQuotesWithEligibility,
      underwritingResults,
      expandedIndices,
      selectedTab,
      clientPartner,
      premiumValue,
      paymentOption,
      advisorId,
      isQuoteSelected,
      handleCardToggle,
      handleViewUnderwritingDetails,
      handleOnClose,
      knownLimitationError,
      hasSameGenderPartner,
      mode,
      benefitDesignQuoteGroups,
      state.stateAbbreviation,
    ],
  );

  const shopInsuranceFiltersProps = {
    showEligibilityFilter: flags.nycLifeDemo ? false : showEligibilityFilter,
    eligibilityCounts,
    mode,
    handleModeChange,
    benefitDesignAvailableOptions,
  };

  const isMarriedOrPartnered =
    mapMaritalStatusToTraditionalLTC(client?.intakeSurvey?.clientMaritalStatus) === 'Married' ||
    hasPartnerWithDemographics;

  const maxCoverageQuote = useMemo(() => {
    if (!adjustedQuotes.length) return undefined;
    return adjustedQuotes.reduce((best, q) =>
      (q.projectedCostCoverage ?? 0) > (best.projectedCostCoverage ?? 0) ? q : best,
    );
  }, [adjustedQuotes]);

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden"
      data-testid="shop-insurance-content"
    >
      <ShopInsuranceContext.Provider
        value={{
          sliderBounds,
          paymentOption,
          handlePaymentOptionChange,
          showTraditionalLTCOptions: getShowTraditionalLTCOptions(
            client.intakeSurvey.clientZipCode,
            client.intakeSurvey.clientBirthDate,
          ),
          showSinglePayAnd10Pay: getShowSinglePayAnd10Pay(
            featureFlags.hideSinglePayAnd10Pay,
            client.intakeSurvey.clientBirthDate,
          ),
          maxCoverageQuote:
            isCoverageGapEnabled && maxCoverageQuote
              ? {
                  premiumAmount: maxCoverageQuote.premiumAmount,
                  projectedCostCoverage: maxCoverageQuote.projectedCostCoverage ?? 0,
                  maxAchievableCoverage:
                    maxCoverageQuote.maxAchievableCoverage ??
                    maxCoverageQuote.projectedCostCoverage ??
                    0,
                  carrierName: maxCoverageQuote.carrierName,
                }
              : undefined,
          isGapFillMode: isCoverageGapEnabled && isGapFillMode,
          setGapFillMode: isCoverageGapEnabled ? setGapFillMode : () => {},
        }}
      >
        <FormProvider {...formMethods}>
          <ShopInsuranceAcknowledgmentOverlay
            open={showAcknowledgment && !hasAcknowledged}
            onAcknowledge={handleAcknowledgment}
          />

          {/* HEADER — sticky above scroll */}
          <div className="border-border bg-background sticky top-0 z-40 shrink-0 border-b px-4">
            <div className="mx-auto w-full max-w-7xl">
              <ShopInsuranceHeader
                isLoading={isLoading}
                error={error}
                onClose={handleOnClose}
              />
            </div>
          </div>

          {/* BODY — scrollable area */}
          <div className="relative min-h-0 flex-1 overflow-y-auto px-4 pt-6 lg:pt-0">
            {isReloading && (
              <div className="bg-background/50 fixed inset-0 z-10 grid h-screen w-screen place-items-center">
                <Loader className="text-primary size-8 animate-spin" />
              </div>
            )}
            <div className="mx-auto w-full max-w-7xl">
              {isInitialLoading && (
                <div
                  className="flex min-h-[60vh] items-center justify-center"
                  data-testid="shop-insurance-loading"
                >
                  <LoadingSpinner testId="shop-insurance-loading-spinner" />
                </div>
              )}
              {hasUnexpectedError && (
                <div className="py-10">
                  <QuoteErrorDisplay
                    error={error}
                    onRetry={() => fetchQuotesForPolicyType(selectedTab, paymentOption)}
                  />
                </div>
              )}

              {isNYStateNotSupported && (
                <NoQuotesNYScreen
                  clientId={client.clientId}
                  advisorId={advisorId}
                  signupSource="shop_insurance"
                />
              )}

              {showContentArea && (
                <div className="flex flex-col lg:flex-row lg:gap-x-8">
                  {/* SIDEBAR — fixed width, own scroll if content overflows */}
                  <ShopInsuranceFiltersSidebar {...shopInsuranceFiltersProps} />

                  {/* RIGHT COLUMN */}
                  <div className="flex flex-1 flex-col">
                    <div className="space-y-3 pt-6 empty:hidden">
                      <AiUnderwritingCtaAlert />
                    </div>
                    {isCoverageGapEnabled && <GapFillBanner />}

                    {mode === 'premium-optimization' &&
                      (isAdjusted.length > 0 ||
                        (!flags.nycLifeDemo &&
                          showEligibilityFilter &&
                          selectedEligibilityFilters.length > 0) ||
                        selectedFeatureBenefitsFilters.length > 0) && (
                        <div className="bg-background sticky top-0 z-50 -mx-1 shrink-0 px-1 pb-2 pt-6">
                          <AdjustedQuotesBanner
                            premiumValue={premiumValue}
                            benefitDurationValue={benefitDurationValue}
                            inflationProtectionValue={inflationProtectionValue}
                            appliedFilters={isAdjusted}
                            selectedEligibilityFilters={
                              flags.nycLifeDemo || !showEligibilityFilter
                                ? []
                                : selectedEligibilityFilters
                            }
                            onRemoveEligibilityFilter={removeEligibilityFilter}
                            selectedFeatureBenefitsFilters={selectedFeatureBenefitsFilters}
                            onRemoveFeatureBenefitsFilter={removeFeatureBenefitsFilter}
                          />
                        </div>
                      )}

                    <div className="pt-6">
                      <ShopInsuranceTabsWrapper
                        hasPartnerWithDemographics={hasPartnerWithDemographics}
                        isMarriedOrPartnered={isMarriedOrPartnered}
                        paymentOption={paymentOption}
                        selectedTab={selectedTab}
                        selectedBuyerOption={selectedBuyerOption}
                        tabContent={tabContent}
                        handleTabChangeWithFetch={handleTabChangeWithFetch}
                        handleBuyerOptionChange={handleBuyerOptionChange}
                        featureFlags={featureFlags}
                        mode={mode}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {showContentArea && (
              <div className="bg-muted mx-auto mb-40 mt-16 w-full max-w-7xl rounded-xl border p-4">
                <ShopInsuranceNotice />
              </div>
            )}
          </div>
        </FormProvider>
      </ShopInsuranceContext.Provider>

      {!flags.nycLifeDemo && (
        <UnderwritingSlideOut
          open={showUnderwritingSlideOut}
          onClose={() => setShowUnderwritingSlideOut(false)}
          underwritingData={selectedUnderwritingData?.data || null}
          productName={selectedUnderwritingData?.productName || ''}
        />
      )}

      {hasSelectedPolicies && (
        <div className="pointer-events-none fixed bottom-0 left-0 right-0 z-20 flex justify-center md:bottom-6">
          <div className="bg-background pointer-events-auto flex w-full flex-wrap gap-2 rounded-none border-t px-3 py-4 shadow-xl shadow-black/5 md:w-max md:flex-nowrap md:rounded-full md:border md:py-3">
            <Button
              size="lg"
              variant="outline"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() =>
                navigate(`/clients/${client.clientId}/onboarding/policy-application-dashboard`)
              }
            >
              Start Application
            </Button>
            <Button
              size="lg"
              className="h-10 rounded-full px-4 text-sm md:h-11 md:px-6 md:text-base"
              onClick={() => setShowComparePolicies(true)}
            >
              Compare Policies
            </Button>
          </div>
        </div>
      )}

      <PoliciesComparisonModal
        open={showComparePolicies}
        onOpenChange={setShowComparePolicies}
      />

      {!isD2C && (
        <>
          <PartnerRequiredDialog
            open={showPartnerRequiredDialog}
            onOpenChange={setShowPartnerRequiredDialog}
            onTakeMeToIt={() => {
              setShowPartnerRequiredDialog(false);
              setShowAddPartnerDialog(true);
            }}
            onLinkExisting={
              isAdvisor
                ? () => {
                    setShowPartnerRequiredDialog(false);
                    setShowLinkExistingDialog(true);
                  }
                : undefined
            }
          />

          {isAdvisor && (
            <LinkExistingPartnerDialog
              open={showLinkExistingDialog}
              client={client}
              onClose={() => setShowLinkExistingDialog(false)}
            />
          )}

          <AddPartnerDialog
            open={showAddPartnerDialog}
            onOpenChange={setShowAddPartnerDialog}
            clientId={client.clientId}
            onSubmitSuccess={() => {
              setShowAddPartnerDialog(false);
              setSelectedTab('joint');
              setExpandedIndices([]);
            }}
          />
        </>
      )}
    </div>
  );
}
